import { all, call, put, select, takeLatest } from "redux-saga/effects";
import { http } from "../../common/lib";
import config from "../../config";
import { actionCreators, actionTypes } from "./actions";
import { logToSentry } from "../../config/Sentry";

function* get() {
    try {
        const { id } = yield select((state) => state.userOrganisations.item);
        const url = `${config.api.paths.auditGroups}?organisation=${id}`;
        const { data } = yield call(http.authorized.get, url);
        yield put(actionCreators.getSuccess(data));
    } catch (error) {
        const errStr = error.toString();
        logToSentry("On audit groups get:", { error });
        console.error(error);
        yield put(actionCreators.getFailure(error));
    }
}

function* post(action) {
    const { auditGroup } = action.payload;
    try {
        const { id } = yield select((state) => state.userOrganisations.item);
        const { data } = yield call(
            http.authorized.post,
            config.api.paths.auditGroups,
            {
                ...auditGroup,
                organisation: id
            }
        );
        yield put(actionCreators.postSuccess(data));
    } catch (error) {
        const errStr = error.toString();
        logToSentry("On audit groups post:", { error });
        console.error(error);
        yield put(actionCreators.postFailure(error));
    }
}

function* patch(action) {
    const { auditGroup } = action.payload;
    const url = `${config.api.paths.auditGroups}${auditGroup.id}/`;
    try {
        const { id } = yield select((state) => state.userOrganisations.item);
        const { data } = yield call(http.authorized.patch, url, {
            ...auditGroup,
            organisation: id
        });
        yield put(actionCreators.patchSuccess(data));
    } catch (error) {
        const errStr = error.toString();
        logToSentry("On audit groups patch:", { error });
        console.error(error);
        yield put(actionCreators.patchFailure(error));
    }
}

// Two recommendations "agree" if they'd result in the same Group: same real
// group id, or -- for a virtual (not-yet-created) preview, which always has
// id === null -- the same rule and rendered name. Never compare on name
// alone: two different rules could coincidentally render the same text.
const isSameRecommendation = (a: any, b: any) => {
    if (!a || !b) return false;
    if (a.id || b.id) return a.id === b.id;
    return a.ruleId === b.ruleId && a.name === b.name;
};

/**
 * Fetch the recommended-group preview for every branch in this audit batch
 * and reconcile them into one. A batch can span multiple branches (the
 * Branches step is multi-select); a recommendation is inherently per-branch,
 * so if they disagree there is no single correct answer to show -- the
 * wizard falls back to today's plain manual dropdown (recommendation: null)
 * rather than pick one arbitrarily or guess.
 *
 * Never itself creates a Group -- GET /recommended-group/ is read-only. See
 * that endpoint's docstring (audit.services.recommend_group_for_branch) for
 * why: the recommendation is a preview, and the real Group is only created
 * when the audit is actually submitted.
 */
function* getRecommendation(action) {
    const { branchIds } = action.payload;
    if (!branchIds || branchIds.length === 0) {
        yield put(actionCreators.getRecommendationSuccess(null));
        return;
    }
    try {
        const { id: organisationId } = yield select(
            (state) => state.userOrganisations.item
        );
        const responses = yield all(
            branchIds.map((branchId) =>
                call(
                    http.authorized.get,
                    `${config.api.paths.auditGroupRecommendation}?organisation=${organisationId}&branch=${branchId}`
                )
            )
        );
        const [first, ...rest] = responses.map((response) => response.data);
        const allBranchesAgree = rest.every((recommendation) =>
            isSameRecommendation(first, recommendation)
        );
        yield put(
            actionCreators.getRecommendationSuccess(
                allBranchesAgree ? { ...first, isRecommended: true } : null
            )
        );
    } catch (error) {
        logToSentry("On audit group recommendation get:", { error });
        console.error(error);
        yield put(actionCreators.getRecommendationFailure(error));
    }
}

export default function* () {
    yield all([
        takeLatest(actionTypes.AUDIT_GROUPS_GET_REQUEST, get),
        takeLatest(actionTypes.AUDIT_GROUP_POST_REQUEST, post),
        takeLatest(actionTypes.AUDIT_GROUP_PATCH_REQUEST, patch),
        takeLatest(
            actionTypes.AUDIT_GROUP_RECOMMENDATION_GET_REQUEST,
            getRecommendation
        )
    ]);
}
